/**
 * API Service for Maps Knowledge Graph
 * Connects to backend API instead of direct Neo4j connection
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Run a search query via backend API
 * Supports both API-style calls and legacy Cypher query strings
 * @param {string} queryOrType - Either a query type string or raw Cypher query
 * @param {object} params - Query parameters
 * @returns {Promise<Array>} Query results
 */
export async function runQuery(queryOrType, params = {}) {
  // Handle legacy Cypher query strings (convert to API calls where possible)
  if (typeof queryOrType === 'string' && queryOrType.trim().toUpperCase().startsWith('MATCH')) {
    console.warn('Legacy Cypher query detected, converting to API call');
    
    // Extract place parameter for simple place searches
    if (params.place && queryOrType.includes('n.place_name') && queryOrType.includes('n.unit_name')) {
      return await searchPlace(params.place);
    }
    
    // For autocomplete queries, return empty array (will be handled by autocomplete endpoint)
    if (queryOrType.includes('COALESCE(n.place_name, n.unit_name)') && queryOrType.includes('LIMIT 1000')) {
      // Return all place names for autocomplete
      return [];
    }
    
    // For relationship type queries
    if (queryOrType.includes('type(r)') && queryOrType.includes('relationshipType')) {
      const types = await getRelationshipTypes();
      return types.map(type => ({ relationshipType: type }));
    }
    
    // Fallback for unsupported queries
    console.warn('Cypher query not fully supported by API, returning empty array');
    return [];
  }
  
  // Handle API-style query type strings
  let endpoint;
  
  switch (queryOrType) {
    case 'place':
      endpoint = `/search/place?name=${encodeURIComponent(params.name || '')}&limit=${params.limit || 100}`;
      break;
    
    case 'type-relation':
      endpoint = `/search/type-relation?place=${encodeURIComponent(params.place || '')}&placeType=${encodeURIComponent(params.placeType || '')}&relationType=${encodeURIComponent(params.relationType || '')}&limit=${params.limit || 100}`;
      break;
    
    case 'type-in-place':
      endpoint = `/search/type-in-place?place=${encodeURIComponent(params.place || '')}&placeType=${encodeURIComponent(params.placeType || '')}&limit=${params.limit || 100}`;
      break;
    
    case 'autocomplete':
      endpoint = `/search/autocomplete?query=${encodeURIComponent(params.query || '')}&limit=${params.limit || 10}`;
      break;
    
    case 'relationship-types':
      endpoint = `/relationships/types`;
      break;
    
    case 'stats':
      endpoint = `/stats`;
      break;
      
    case 'health':
      endpoint = `/health`;
      break;
    
    default:
      throw new Error(`Unknown query type: ${queryOrType}`);
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API query error:', error);
    throw error;
  }
}

/**
 * Get autocomplete suggestions
 * @param {string} query - Search query
 * @param {number} limit - Maximum results
 * @returns {Promise<string[]>} Array of suggestions
 */
export async function getAutocompleteSuggestions(query, limit = 10) {
  if (!query || query.length < 2) {
    return [];
  }
  
  return await runQuery('autocomplete', { query, limit });
}

/**
 * Search for a place by name
 * @param {string} name - Place name
 * @param {number} limit - Maximum results
 * @returns {Promise<Array>} Query results
 */
export async function searchPlace(name, limit = 100) {
  return await runQuery('place', { name, limit });
}

/**
 * Search by place type and relationship
 * @param {string} place - Place name
 * @param {string} placeType - Type of place
 * @param {string} relationType - Relationship type
 * @param {number} limit - Maximum results
 * @returns {Promise<Array>} Query results
 */
export async function searchTypeRelation(place, placeType, relationType, limit = 100) {
  return await runQuery('type-relation', { place, placeType, relationType, limit });
}

/**
 * Find all of a type in a place
 * @param {string} place - Place name
 * @param {string} placeType - Type of place
 * @param {number} limit - Maximum results
 * @returns {Promise<Array>} Query results
 */
export async function searchTypeInPlace(place, placeType, limit = 100) {
  return await runQuery('type-in-place', { place, placeType, limit });
}

/**
 * Get available relationship types
 * @returns {Promise<string[]>} Array of relationship types
 */
export async function getRelationshipTypes() {
  return await runQuery('relationship-types');
}

/**
 * Get database statistics
 * @returns {Promise<object>} Database statistics
 */
export async function getDatabaseStats() {
  return await runQuery('stats');
}

/**
 * Check API health
 * @returns {Promise<object>} Health status
 */
export async function checkHealth() {
  return await runQuery('health');
}
